/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Button, CircularProgress, Typography, useMediaQuery } from "@mui/material";
import UpdateImageUpload from "../../components/inputs/uploadImageUpdate";
import CustomInput from "../../components/inputs/customInput";
import { fileApiRoute } from "../../services/networkService";
import StatusDropdown from "../../components/dropdowns/statusDropdown";
import BranchDropdown from "../../components/dropdowns/branchDropdown";
import DriveFileRenameOutlineOutlinedIcon from '@mui/icons-material/DriveFileRenameOutlineOutlined';
import { useTheme } from "@emotion/react";
import Popup from "../../components/popups/popup";
import { useNavigate, useParams } from "react-router-dom";
import { v4 as uuidv4 } from 'uuid';
import PageService from "../../services/pageService";
import CityPageListForm from "../../components/subFroms/cityPageListForm";

const UpdatePage = () => {
    const { t } = useTranslation();
    const theme = useTheme();
    const navigate = useNavigate();
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));

    const { id } = useParams();

    const [page, setPage] = useState({});
    const [selectedBranch, setSelectedBranch] = useState(null);
    const [selectedStatus, setSelectedStatus] = useState('');
    const [selectedImage, setSelectedImage] = useState(null);
    const [selectedIDImage, setSelectedIDImage] = useState(null);

    const [formValues, setFormValues] = useState({
        name: page.name,
        ownerName: page.ownerName,
        phone: page.phone,
        phoneTwo: page.phoneTwo,
        password: '',
        confirmPassword: '',
        instgram: page.instgram,
        facebook: page.facebook,
        note: page.note,
        branchId: page.branchId,
        status: page.status,
        prices: page.cityPages,
        deleteImage: false,
        deleteIDImage: false
    });

    const [updateErrors, setUpdateErrors] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [isModalLoading, setIsModalLoading] = useState(true);

    const [showPopup, setShowPopup] = useState(false);
    const [popupMsg, setPopupMsg] = useState(false);
    const [popupType, setPopupType] = useState(false);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormValues((prevValues) => ({
            ...prevValues,
            [name]: value,
        }));
    };

    const handleImageChange = (file) => {
        setSelectedImage(file);
    };

    const handleIDImageChange = (file) => {
        setSelectedIDImage(file);
    };

    const validateForm = () => {
        const newUpdateErrors = {};

        if (formValues.password && formValues.password.length < 8) {
            newUpdateErrors.password = t('passwordMin');
        }

        if (formValues.password !== formValues.confirmPassword) {
            newUpdateErrors.confirmPassword = t('confirmPasswordMatch');
        }

        formValues.prices.forEach((price, index) => {
            if (!price.cityId) price.cityError = t('cityRequired');
            else price.cityError = "";
            if (!price.transportationPrice) price.transportationPriceError = t('transportationPriceRequired');
            else price.transportationPriceError = "";
            if (isNaN(price.transportationPrice)) price.transportationPriceError = t('transportationPriceNumber');
        });

        setUpdateErrors(newUpdateErrors);

        const checkError = formValues.prices.some((price) => (price.cityError !== "" || price.transportationPriceError !== ""));

        return Object.keys(newUpdateErrors).length === 0 && !checkError;
    };


    const handleSubmit = async (e) => {
        e.preventDefault();

        if (isLoading) return;

        if (validateForm()) {
            try {
                setIsLoading(true);
                const formData = new FormData();

                const { prices, ...formInfo } = formValues;

                for (const key in formInfo) {
                    if (formInfo[key] && formInfo[key] !== '') {
                        formData.append(key, formInfo[key]);
                    }
                }

                prices.forEach((price, index) => {
                    formData.append(`cities[${index}][cityId]`, price.cityId);
                    formData.append(`cities[${index}][transportationPrice]`, price.transportationPrice);
                });

                if (selectedImage) {
                    formData.append('image', selectedImage);
                }

                if (selectedIDImage) {
                    formData.append('IDImage', selectedIDImage);
                }

                const data = await PageService.update(id, formData);
                if (data.status === 200) {
                    setPopupMsg(data.data.message);
                    setPopupType('success');
                    setShowPopup(true);
                    navigate('/pages');
                }

                setUpdateErrors({});
            } catch (error) {
                if (error.response?.status === 400) {
                    const responseErrors = error.response.data || {};
                    const formattedErrors = {};

                    Object.keys(responseErrors).forEach((field) => {
                        formattedErrors[field] = responseErrors[field];
                    });

                    setUpdateErrors(formattedErrors);
                } else {
                    setPopupMsg(error.response.data.error);
                    setPopupType('error');
                    console.error("An unexpected error occurred:", error);
                    setUpdateErrors({ general: error.response.data.error });
                }
            } finally {
                setIsLoading(false);
            }
        }
    };

    const fetchPage = async () => {
        try {
            const data = await PageService.fetchOne(id);

            setPage(data);
            setIsModalLoading(false);
        } catch (error) {
            console.error('Error fetching page:', error);
        }
    };

    useEffect(() => {
        if (page) {
            setFormValues({
                name: page.name || '',
                phone: page.phone || '',
                phoneTwo: page.phoneTwo || '',
                note: page.note || '',
                branchId: page.branchId || '',
                ownerName: page.ownerName || '',
                instgram: page.instgram || '',
                facebook: page.facebook || '',
                prices: (page.cityPages || []).map((city) => ({
                    id: uuidv4(),
                    cityId: city.city.id,
                    name: city.city.name,
                    transportationPrice: city.transportationPrice || "",
                    cityError: "",
                    transportationPriceError: "",
                })),
            });
            setSelectedBranch(page.branch || null);
            setSelectedStatus(t(page.status) || '');
            setUpdateErrors({});
        }
    }, [page]);


    useEffect(() => {
        setIsModalLoading(true);
        fetchPage();
    }, []);



    return (
        <Box sx={{
            p: isTablet ? 0.6 : 2,
            backgroundColor: theme.palette.secondary.main,
        }}>
            {showPopup && (
                <Popup
                    text={popupMsg}
                    type={popupType}
                    onClose={() => setShowPopup(false)}
                />
            )}

            <Box
                component="form"
                onSubmit={handleSubmit}
                sx={{
                    width: '100%',
                    bgcolor: 'background.paper',
                    borderRadius: 2,
                    border: 1,
                    borderColor: theme.palette.customGrey.borderInput,
                    p: isTablet ? 1 : 4
                }}
            >
                {
                    isModalLoading ? <Box sx={{ justifyContent: 'center', display: 'flex', my: 10 }} >
                        <CircularProgress color="primary" size={28} />
                    </Box>
                        : <Box>
                            <Box sx={{
                                display: 'flex',
                                justifyContent: 'space-between'
                            }}>
                                <Typography id="transition-modal-title" variant="h6" component="h2">
                                    {t('updatePage')}
                                </Typography>
                            </Box>

                            {/* row 1 */}
                            <Box sx={{
                                display: isTablet ? 'unset' : 'flex',
                                justifyContent: 'space-between'
                            }}>
                                {/* Branch */}
                                <Box width={isTablet ? '100%' : '32%'}>
                                    <BranchDropdown
                                        helperText={updateErrors.branch}
                                        error={!!updateErrors.branch}
                                        setFormValues={setFormValues}
                                        isFilter={false}
                                        selectedBranch={selectedBranch}
                                        setSelectedBranch={setSelectedBranch}
                                    />
                                </Box>

                                {/* Status */}
                                <Box width={isTablet ? '100%' : '32%'}>
                                    <StatusDropdown
                                        helperText={updateErrors.status}
                                        error={!!updateErrors.status}
                                        setFormValues={setFormValues}
                                        isFilter={false}
                                        selectedStatus={selectedStatus}
                                        setSelectedStatus={setSelectedStatus}
                                    />
                                </Box>

                                <Box width={isTablet ? '100%' : '32%'}>
                                    <CustomInput
                                        label={t('name')}
                                        placeholder={t('nameHere')}
                                        name="name"
                                        type='text'
                                        value={formValues.name}
                                        error={!!updateErrors.name}
                                        helperText={updateErrors.name}
                                        onChange={handleChange}
                                    />
                                </Box>

                            </Box>


                            {/* row 2 */}
                            <Box sx={{
                                display: isTablet ? 'unset' : 'flex',
                                justifyContent: 'space-between'
                            }}>
                                <Box width={isTablet ? '100%' : '32%'}>
                                    <CustomInput
                                        label={t('ownerName')}
                                        placeholder={t('ownerNameHere')}
                                        name="ownerName"
                                        type='text'
                                        value={formValues.ownerName}
                                        error={!!updateErrors.ownerName}
                                        helperText={updateErrors.ownerName}
                                        onChange={handleChange}
                                    />
                                </Box>

                                <Box width={isTablet ? '100%' : '32%'}>
                                    <CustomInput
                                        label={t('instgram')}
                                        placeholder={t('instgramHere')}
                                        name="instgram"
                                        type='text'
                                        value={formValues.instgram}
                                        onChange={handleChange}
                                    />
                                </Box>

                                <Box width={isTablet ? '100%' : '32%'}>
                                    <CustomInput
                                        label={t('facebook')}
                                        placeholder={t('facebookHere')}
                                        name="facebook"
                                        type='text'
                                        value={formValues.facebook}
                                        onChange={handleChange}
                                    />
                                </Box>
                            </Box>


                            {/* row 3 */}
                            <Box sx={{
                                display: isTablet ? 'unset' : 'flex',
                                justifyContent: 'space-between'
                            }}>
                                <Box width={isTablet ? '100%' : '32%'}>
                                    <CustomInput
                                        label={t('phone')}
                                        placeholder={t('phoneHere')}
                                        name="phone"
                                        type='text'
                                        value={formValues.phone}
                                        error={!!updateErrors.phone}
                                        helperText={updateErrors.phone}
                                        onChange={handleChange}
                                    />
                                </Box>

                                <Box width={isTablet ? '100%' : '32%'}>
                                    <CustomInput
                                        label={t('phoneTwo')}
                                        placeholder={t('phoneTwoHere')}
                                        name="phoneTwo"
                                        type='text'
                                        value={formValues.phoneTwo}
                                        onChange={handleChange}
                                    />
                                </Box>

                                <Box width={isTablet ? '100%' : '32%'}>
                                    <CustomInput
                                        label={t('note')}
                                        placeholder={t('noteHere')}
                                        name="note"
                                        type='text'
                                        value={formValues.note}
                                        onChange={handleChange}
                                    />
                                </Box>
                            </Box>


                            {/* row 4 */}
                            <Box sx={{
                                display: isTablet ? 'unset' : 'flex',
                                justifyContent: 'space-between'
                            }}>
                                <Box width={isTablet ? '100%' : '32%'}>
                                    <CustomInput
                                        label={t('password')}
                                        placeholder="******"
                                        name="password"
                                        type="password"
                                        value={formValues.password}
                                        onChange={handleChange}
                                        error={!!updateErrors.password}
                                        helperText={updateErrors.password}
                                    /></Box>

                                <Box width={isTablet ? '100%' : '32%'}>
                                    <CustomInput
                                        label={t('confirmPassword')}
                                        placeholder="******"
                                        name="confirmPassword"
                                        type="password"
                                        value={formValues.confirmPassword}
                                        onChange={handleChange}
                                        error={!!updateErrors.confirmPassword}
                                        helperText={updateErrors.confirmPassword}
                                    /></Box>

                                <Box width={isTablet ? '100%' : '32%'}>
                                </Box>
                            </Box>




                            {/* images */}
                            <Box
                                sx={{
                                    display: isTablet ? 'unset' : 'flex',
                                    justifyContent: 'space-around',
                                    width: '100%'
                                }}>
                                <Box sx={{ width: isTablet ? '100%' : '30vw' }}>
                                    <UpdateImageUpload
                                        label={t('uploadImage')}
                                        name={t('image')}
                                        value={'deleteImage'}
                                        onChange={handleImageChange}
                                        image={`${fileApiRoute}/${page.image}`}
                                        setFormValues={setFormValues}
                                    />
                                </Box>

                                <Box sx={{ width: isTablet ? '100%' : '30vw' }}>
                                    <UpdateImageUpload
                                        label={t('uploadIDImage')}
                                        name={t('IDimage')}
                                        value={'deleteIDImage'}
                                        onChange={handleIDImageChange}
                                        image={`${fileApiRoute}/${page.IDImage}`}
                                        setFormValues={setFormValues}
                                    />
                                </Box>

                            </Box>

                            {/* row 6 prices */}
                            <CityPageListForm
                                formValues={formValues}
                                setFormValues={setFormValues}
                            />

                            {updateErrors.general && (
                                <Typography align="center" color="error" variant="body2" sx={{ mt: 2 }}>
                                    {updateErrors.general}
                                </Typography>
                            )}

                            <Box sx={{
                                display: 'flex', justifyContent: 'center', alignItems: 'center',
                                mt: updateErrors.general ? 2 : 4
                            }}>
                                <Button
                                    type="submit"
                                    variant="contained"
                                    color="primary"
                                    sx={{ textTransform: 'none', width: 140, height: 40 }}
                                >
                                    {isLoading ? <CircularProgress color="secondary" size={28} /> :
                                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                            <p>{t('update')}</p>
                                            <DriveFileRenameOutlineOutlinedIcon fontSize="small" sx={{ mx: 0.4 }} />
                                        </Box>
                                    }
                                </Button>
                            </Box>
                        </Box>
                }

            </Box>
        </Box>
    );
}

export default UpdatePage;